/**
 * AI Module — ONNX inference + clinical algorithms
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [ULTIMATE GOD MODE EDITION v10.3 — UNIFIED CORE]
 * 
 * v10.3 REFACTORING:
 * - ✅ Удалены дубликаты функций (estimateEF, tumorKinetics, segmentOrgansByHU, etc.)
 * - ✅ Импорт из aiModuleCore.js (единый источник правды)
 * - ✅ Реэкспорт для обратной совместимости
 * - ✅ BFS fixes (visited при enqueue)
 * - ✅ Zero-GC Tensor Pool в runSegmentation3DLoop
 * - ✅ DICOM SR массив ContentSequence
 * - ✅ _yieldToMain во всех тяжёлых операциях
 */

import { log } from '../utils/logger.js';
import { calciumScore, agatstonWeight } from './calciumScoreCore.js';

// ══════════════════════════════════════════════════════════════
//  ИМПОРТ ИЗ CORE (единый источник правды)
// ══════════════════════════════════════════════════════════════

import {
  estimateEF,
  tumorKinetics,
  aortaAneurysmScreening,
  segmentOrgansByHU,
  segmentBrainByHU,
  incidentalFindingsScreening,
} from './aiModuleCore.js';

// Реэкспорт для обратной совместимости
export {
  estimateEF,
  tumorKinetics,
  aortaAneurysmScreening,
  segmentOrgansByHU,
  segmentBrainByHU,
  incidentalFindingsScreening,
  agatstonWeight,
};

let ort = null;
let _ortPromise = null;

const _yieldToMain = () => new Promise(resolve => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(resolve);
  } else {
    setTimeout(resolve, 0);
  }
});

// ══════════════════════════════════════════════════════════════
// ПЕРЕИСПОЛЬЗУЕМЫЕ БУФЕРЫ ПАМЯТИ (Zero-GC Pools)
// ══════════════════════════════════════════════════════════════

let _cclLabelsPool = null;
let _cclQueuePool = null;
let _resizeBufPool = null;
let _resizeBufInUse = false;
let _noduleBfsQueueX = null;
let _noduleBfsQueueY = null;
let _noduleVisitedMarks = null;
let _noduleVisitId = 0;

function _getCCLPools(size) {
  if (!_cclLabelsPool || _cclLabelsPool.length < size) {
    log.debug(`[AI] Allocating CCL pool: ${((size * 4 * 2) / 1048576).toFixed(1)} MB`);
    _cclLabelsPool = new Int32Array(size);
    _cclQueuePool = new Int32Array(size);
  }
  _cclLabelsPool.fill(0);
  return { labels: _cclLabelsPool, queue: _cclQueuePool };
}

function _getResizeBuf(size) {
  if (!_resizeBufPool || _resizeBufPool.length < size) {
    _resizeBufPool = new Float32Array(size);
  }
  _resizeBufInUse = true;
  return _resizeBufPool;
}

function _releaseResizeBuf() {
  _resizeBufInUse = false;
}

function _getNoduleBfsBuffers(sliceArea) {
  if (!_noduleBfsQueueX || _noduleBfsQueueX.length < sliceArea) {
    _noduleBfsQueueX = new Int32Array(sliceArea);
    _noduleBfsQueueY = new Int32Array(sliceArea);
    _noduleVisitedMarks = new Uint32Array(sliceArea);
    _noduleVisitId = 0;
  }
  _noduleVisitId++;
  if (_noduleVisitId >= 4294967290) {
    _noduleVisitedMarks.fill(0);
    _noduleVisitId = 1;
  }
  return {
    queueX: _noduleBfsQueueX,
    queueY: _noduleBfsQueueY,
    visitedMarks: _noduleVisitedMarks,
    visitId: _noduleVisitId
  };
}

// ══════════════════════════════════════════════════════════════
// ONNX RUNTIME INIT & MODEL REGISTRY
// ══════════════════════════════════════════════════════════════

async function _ensureOrt() {
  if (ort) return ort;
  if (_ortPromise) return _ortPromise;

  _ortPromise = (async () => {
    try {
      const m = await import('onnxruntime-web/webgpu');
      ort = m.default || m;
      if (ort?.env) {
        ort.env.wasm.wasmPaths = '/ort-wasm/';
        ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency || 4, 4);
      }
      log.debug('[AI] ONNX Runtime loaded with WebGPU');
    } catch (e) {
      log.warn('[AI] WebGPU failed, fallback to WASM:', e.message);
      try {
        const m2 = await import('onnxruntime-web');
        ort = m2.default || m2;
        if (ort?.env) {
          ort.env.wasm.wasmPaths = '/ort-wasm/';
          ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency || 4, 4);
        }
      } catch (e2) {
        log.error('[AI] ONNX Runtime unavailable:', e2.message);
        ort = null;
      }
    }
    return ort;
  })();
  return _ortPromise;
}

const _sessions = new Map();
const _modelUrls = {
  'cardiac-chambers': '/models/cardiac_chambers.onnx',
  'calcium-detect': '/models/calcium_detect.onnx',
  'medsam-encoder': '/models/vision_encoder_quantized.onnx',
  'medsam-decoder': '/models/sam_vit_b_01ec64.decoder.onnx',
  'lungmask': '/models/lungmask_r231.onnx',
  'lung-seg': '/models/lungmask_r231.onnx',
  'cardiac-seg': '/models/cardiac_chambers.onnx',
};

const _aliasMap = {
  'lung-seg': 'lungmask',
  'cardiac-seg': 'cardiac-chambers',
  'calcium-scoring': 'calcium-detect',
  'agatston': 'calcium-detect'
};

function _canonicalModelId(modelId) {
  return _aliasMap[modelId] || modelId;
}

const _algorithmicFallbacks = new Set([
  'cardiac-seg', 'cardiac-chambers', 'organ-seg', 'brain-seg',
  // [11.0.2] 'lung-seg' добавлена: она была ЕДИНСТВЕННОЙ из семи карточек ИИ,
  // не попавшей в этот список, и потому второй обработчик (clinicalTools)
  // не отсекался и лез за файлом lungmask_r231.onnx параллельно роутеру.
  // Откат у неё такой же настоящий, как у соседей: лёгкие выделяются по
  // порогам HU внутри segmentOrgansByHU (featureBindings, case 'lung-seg').
  'lung-seg',
  'rt-contour', 'lung-nodule', 'stroke-detect', 'calcium-scoring', 'agatston',
]);

export function hasAlgorithmicFallback(modelId) {
  return _algorithmicFallbacks.has(modelId);
}

export async function loadModel(modelId) {
  try {
    modelId = _canonicalModelId(modelId);
    await _ensureOrt();
    if (!ort) { log.warn('[AI] ONNX not loaded'); return null; }
    if (_sessions.has(modelId)) return _sessions.get(modelId);

    const url = _modelUrls[modelId];
    if (!url) throw new Error(`Unknown model: ${modelId}`);

    log.debug(`[AI] Loading model: ${modelId}`);
    const session = await ort.InferenceSession.create(url, {
      executionProviders: ['webgpu', 'wasm'],
      graphOptimizationLevel: 'all',
    });
    _sessions.set(modelId, session);
    return session;
  } catch (e) { log.warn(`[aiModule] loadModel failed:`, e.message); throw e; }
}

export async function warmupModels() {
  try {
    await _ensureOrt();
    if (!ort) { log.warn('[AI] ONNX not loaded, warmup skipped'); return; }
    
    log.debug('[AI] Warming up models...');
    const size = 1024;
    const dummyInput = new Float32Array(3 * size * size);
    const dummyTensor = new ort.Tensor('float32', dummyInput, [1, 3, size, size]);

    if (_modelUrls['medsam-encoder']) {
      const enc = await loadModel('medsam-encoder');
      await enc.run({ [enc.inputNames[0]]: dummyTensor });
    }
    if (_modelUrls['lungmask']) {
      const lung = await loadModel('lungmask');
      const dummyLung = new Float32Array(256 * 256);
      await lung.run({ [lung.inputNames[0]]: new ort.Tensor('float32', dummyLung, [1, 1, 256, 256]) });
    }
    log.debug('[AI] Warmup complete');
  } catch (e) { log.warn('[AI] Warmup skipped:', e.message); }
}

/**
 * [10.19.9] Что из моделей реально лежит на месте.
 *
 * Зачем отдельной функцией. Файлы моделей в проект не входят (около 400 МБ),
 * их кладёт врач руками, и до сих пор единственным способом узнать, доехали
 * они или нет, было нажать ИИ-кнопку и посмотреть, не сработал ли молча
 * алгоритмический откат. Теперь можно спросить прямо.
 *
 * Проверяется HEAD-запросом по тому же адресу, по которому модель потом
 * грузится, — то есть проверяется именно то, что будет использовано, а не
 * догадка о путях.
 *
 * @returns {Promise<Array<{модель: string, адрес: string, есть: boolean, мб: number|null, откат: boolean}>>}
 */
export async function проверитьМодели() {
  const итог = [];
  const уникальные = [...new Set(Object.keys(_modelUrls).map(_canonicalModelId))];
  for (const id of уникальные) {
    const адрес = _modelUrls[id];
    if (!адрес) continue;
    let есть = false, мб = null;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const r = await fetch(`${адрес}?nocache=${Date.now()}`, { method: 'HEAD', signal: ctrl.signal });
      clearTimeout(t);
      есть = r.ok;
      const len = Number(r.headers.get('content-length'));
      if (Number.isFinite(len) && len > 0) мб = Math.round(len / 1048576);
    } catch (e) { есть = false; }
    итог.push({ модель: id, адрес, есть, мб, откат: hasAlgorithmicFallback(id) });
  }
  return итог;
}

/** Человеческая сводка по моделям — для кнопки в «Диагностике». */
export async function сводкаПоМоделям() {
  const список = await проверитьМодели();
  const нашлось = список.filter((м) => м.есть);
  const нет = список.filter((м) => !м.есть);
  const безОтката = нет.filter((м) => !м.откат);
  return [
    `Найдено моделей: ${нашлось.length} из ${список.length}.`,
    нашлось.length ? '✅ ' + нашлось.map((м) => `${м.модель}${м.мб ? ` (${м.мб} МБ)` : ''}`).join(', ') : '',
    нет.length ? '❌ нет: ' + нет.map((м) => м.модель + (м.откат ? ' — но есть алгоритмический откат, считается без сети' : '')).join('; ') : '',
    безОтката.length
      ? `Без модели НЕ РАБОТАЕТ: ${безОтката.map((м) => м.модель).join(', ')}.`
      : 'Всё, что нужно, на месте.',
    нет.length ? 'Файлы кладутся в public/models/ или в корень проекта — сборка забирает оба места.' : '',
  ].filter(Boolean).join('\n');
}

export async function isModelAvailable(modelId) {
  modelId = _canonicalModelId(modelId);
  await _ensureOrt();
  if (!ort || !_modelUrls[modelId]) return false;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const resp = await fetch(`${_modelUrls[modelId]}?nocache=${Date.now()}`, { method: 'HEAD', signal: ctrl.signal });
    clearTimeout(t);
    return resp.ok;
  } catch { return false; }
}

// ══════════════════════════════════════════════════════════════
// GENERIC INFERENCE & HIGH-PERFORMANCE 3D BATCHING
// ══════════════════════════════════════════════════════════════

export async function runSegmentation(pixelData, modelId, options = {}) {
  await _ensureOrt();
  const { dims = [1, 1, 128, 128, 128] } = options;
  const session = await loadModel(modelId);
  try {
    const tensor = new ort.Tensor('float32', pixelData, dims);
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("AI Timeout")), 45000));
    const result = await Promise.race([session.run({ [session.inputNames[0]]: tensor }), timeout]);
    return result[session.outputNames[0]].data;
  } catch (e) { log.error('[AI] runSegmentation error:', e); throw e; }
}

export async function runSegmentation3DLoop(scalarData, dims, modelId, options = {}) {
  try {
    await _ensureOrt();
    const { targetDim = 256, numClasses = 3, huMin = -1024, huMax = 600, onProgress, batchSize = 4 } = options;
    const [nx, ny, nz] = dims;
    const sliceSize = nx * ny;
    const huRange = huMax - huMin || 1;
    const session = await loadModel(modelId);
    const inputName = session.inputNames[0];
    const outputName = session.outputNames[0];
    const labelMap = new Uint8Array(nx * ny * nz);
    let lastYield = performance.now();

    // TENSOR POOL: переиспользуем буферы (Zero-GC)
    const tensorPool = Array.from({ length: batchSize }, () => new Float32Array(targetDim * targetDim));
    const resizeBuf = _getResizeBuf(targetDim * targetDim);

    for (let z = 0; z < nz; z += batchSize) {
      const batch = [];
      const indices = [];
      
      for (let b = 0; b < batchSize && z + b < nz; b++) {
        const cz = z + b;
        const srcOff = cz * sliceSize;
        
        for (let ty = 0; ty < targetDim; ty++) {
          const sy = Math.min(Math.floor((ty / targetDim) * ny), ny - 1);
          for (let tx = 0; tx < targetDim; tx++) {
            const sx = Math.min(Math.floor((tx / targetDim) * nx), nx - 1);
            let hu = scalarData[srcOff + sy * nx + sx];
            hu = Math.max(huMin, Math.min(huMax, hu));
            resizeBuf[ty * targetDim + tx] = (hu - huMin) / huRange;
          }
        }
        
        const tensorBuf = tensorPool[b];
        tensorBuf.set(resizeBuf.subarray(0, targetDim * targetDim));
        
        batch.push(session.run({ [inputName]: new ort.Tensor('float32', tensorBuf, [1, 1, targetDim, targetDim]) }));
        indices.push(cz);
      }

      const results = await Promise.all(batch);
      for (let i = 0; i < results.length; i++) {
        const cz = indices[i];
        const srcOff = cz * sliceSize;
        const mask = results[i][outputName].data;
        const pixCount = targetDim * targetDim;
        
        for (let oy = 0; oy < ny; oy++) {
          const ty = Math.min(Math.floor((oy / ny) * targetDim), targetDim - 1);
          for (let ox = 0; ox < nx; ox++) {
            const tx = Math.min(Math.floor((ox / nx) * targetDim), targetDim - 1);
            const idx = ty * targetDim + tx;
            let bestClass = 0, bestVal = mask[idx];
            for (let c = 1; c < numClasses; c++) {
              if (mask[c * pixCount + idx] > bestVal) { bestVal = mask[c * pixCount + idx]; bestClass = c; }
            }
            labelMap[srcOff + oy * nx + ox] = bestClass;
          }
        }
      }

      if (performance.now() - lastYield > 15) {
        onProgress?.(Math.round((z + batchSize) / nz * 100));
        await _yieldToMain();
        lastYield = performance.now();
      }
    }
    
    _releaseResizeBuf();
    return labelMap;
  } catch (e) { log.warn('[aiModule] runSegmentation3DLoop failed:', e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
// CONNECTED COMPONENTS 3D (26-Связность)
// ══════════════════════════════════════════════════════════════

const _neighborOffsetCache = {};

function _getNeighborOffsets(connectivity) {
  if (_neighborOffsetCache[connectivity]) return _neighborOffsetCache[connectivity];
  
  const dx = [], dy = [], dz = [];
  for (let ddz = -1; ddz <= 1; ddz++) {
    for (let ddy = -1; ddy <= 1; ddy++) {
      for (let ddx = -1; ddx <= 1; ddx++) {
        if (ddx === 0 && ddy === 0 && ddz === 0) continue;
        const absSum = Math.abs(ddx) + Math.abs(ddy) + Math.abs(ddz);
        if (connectivity === 6 && absSum > 1) continue;
        if (connectivity === 18 && absSum > 2) continue;
        dx.push(ddx);
        dy.push(ddy);
        dz.push(ddz);
      }
    }
  }
  
  const offsets = {
    dx: new Int8Array(dx),
    dy: new Int8Array(dy),
    dz: new Int8Array(dz),
    count: dx.length
  };
  
  _neighborOffsetCache[connectivity] = offsets;
  return offsets;
}

async function connectedComponents3D(mask, w, h, d, onProgress, options = {}) {
  const { connectivity = 26 } = options;
  const { labels, queue } = _getCCLPools(mask.length);
  const maxTail = queue.length;
  let nextLabel = 1;
  const sliceSize = w * h;
  const idx = (x, y, z) => z * sliceSize + y * w + x;
  let lastYield = performance.now();
  const totalVoxels = d * w * h;

  const offsets = _getNeighborOffsets(connectivity);

  for (let z = 0; z < d; z++) {
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = idx(x, y, z);

        if ((i & 0x1FFFF) === 0 && performance.now() - lastYield > 15) {
          onProgress?.(Math.round((i / totalVoxels) * 50));
          await _yieldToMain();
          lastYield = performance.now();
        }

        if (!mask[i] || labels[i]) continue;

        labels[i] = nextLabel;
        let head = 0, tail = 0;
        queue[tail++] = i;

        while (head < tail) {
          if (tail >= maxTail) {
            throw new Error(`CCL overflow: lesion exceeds pool size ${maxTail}`);
          }

          const ci = queue[head++];
          const cz = Math.floor(ci / sliceSize);
          const cy = Math.floor((ci % sliceSize) / w);
          const cx = ci % w;

          for (let n = 0; n < offsets.count; n++) {
            const nx = cx + offsets.dx[n];
            const ny = cy + offsets.dy[n];
            const nz = cz + offsets.dz[n];

            if (nx >= 0 && nx < w && ny >= 0 && ny < h && nz >= 0 && nz < d) {
              const ni = nz * sliceSize + ny * w + nx;
              if (mask[ni] && !labels[ni]) {
                labels[ni] = nextLabel;
                queue[tail++] = ni;
              }
            }
          }
        }
        nextLabel++;
      }
    }
  }
  return { labels, count: nextLabel - 1 };
}

// ══════════════════════════════════════════════════════════════
// AGATSTON CALCIUM SCORING (делегирование в core)
// ══════════════════════════════════════════════════════════════

export async function calciumScoring(pixels, width, height, depth, spacing, options = {}) {
  try {
    const { onProgress } = options;
    const r = await calciumScore(pixels, width, height, depth, spacing, options);
    const lesions = r.lesions || [];
    const voxelMm3 = (spacing?.[0] || 1) * (spacing?.[1] || 1) * (spacing?.[2] || 1);
    const totalVolumeMm3 = lesions.reduce((a, l) => a + (l.voxels || 0) * voxelMm3, 0);
    const score = r.totalScore || 0;
    let risk, riskLevel;
    if (score === 0) { risk = 'Минимальный'; riskLevel = 0; }
    else if (score < 11) { risk = 'Очень низкий'; riskLevel = 1; }
    else if (score < 101) { risk = 'Умеренный'; riskLevel = 2; }
    else if (score < 401) { risk = 'Высокий'; riskLevel = 3; }
    else { risk = 'Очень высокий'; riskLevel = 4; }
    onProgress?.(100);
    return {
      totalScore: score,
      volumeMm3: Math.round(totalVolumeMm3 * 10) / 10,
      massScore: r.massScore ?? 0,
      lesionCount: r.lesionCount ?? lesions.length,
      risk, riskLevel,
      lesions: lesions.slice(0, 20),
      perSlice: r.perSlice,
      riskCategory: r.riskCategory,
      percentileEstimate: r.percentileEstimate,
    };
  } catch (e) { log.warn('[aiModule] calciumScoring failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// CARDIAC CHAMBERS (O(N) optimization)
// ══════════════════════════════════════════════════════════════

function _getPatientOrientation(orientation) {
  const rowX = orientation[0], rowY = orientation[1], rowZ = orientation[2];
  const colX = orientation[3], colY = orientation[4], colZ = orientation[5];
  
  const normX = rowY * colZ - rowZ * colY;
  const normY = rowZ * colX - rowX * colZ;
  const normZ = rowX * colY - rowY * colX;
  
  const isFlippedX = rowX < -0.01;
  const isAxial = Math.abs(normZ) > 0.9;
  const isCoronal = Math.abs(normY) > 0.9;
  
  return { isFlippedX, isAxial, isCoronal };
}

export async function segmentCardiacChambers(pixels, w, h, d, spacing, options = {}) {
  try {
    const { onProgress, orientation = [1, 0, 0, 0, 1, 0] } = options;
    const total = w * h * d;
    const result = new Uint8Array(total);

    const cx = Math.floor(w / 2), cy = Math.floor(h / 2);
    let centerMaxHU = 0;
    for (let z = Math.floor(d * 0.3); z < Math.floor(d * 0.7); z++) {
      for (let dy_ = -10; dy_ <= 10; dy_++) {
        for (let dx_ = -10; dx_ <= 10; dx_++) {
          const i = z * w * h + (cy + dy_) * w + (cx + dx_);
          if (i >= 0 && i < total && pixels[i] > centerMaxHU) centerMaxHU = pixels[i];
        }
      }
    }

    const hasContrast = centerMaxHU > 200;
    const bloodLow = hasContrast ? 150 : 25;
    const bloodHigh = hasContrast ? 500 : 90;

    const bloodMask = new Uint8Array(total);
    for (let i = 0; i < total; i++) {
      bloodMask[i] = (pixels[i] >= bloodLow && pixels[i] <= bloodHigh) ? 1 : 0;
    }
    onProgress?.(10);

    const { labels, count } = await connectedComponents3D(bloodMask, w, h, d, p => onProgress?.(10 + p * 0.7));
    if (count === 0) { onProgress?.(100); return { mask: result, hasContrast, chambers: {} }; }

    const stats = {};
    for (let i = 1; i <= count; i++) {
      stats[i] = { count: 0, sumX: 0, sumY: 0, sumZ: 0, minZ: d, maxZ: 0 };
    }
    let lastYield = performance.now();

    for (let z = 0; z < d; z++) {
      if (performance.now() - lastYield > 15) { await _yieldToMain(); lastYield = performance.now(); }
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = z * w * h + y * w + x;
          const lbl = labels[i];
          if (!lbl) continue;
          const s = stats[lbl];
          s.count++; s.sumX += x; s.sumY += y; s.sumZ += z;
          if (z < s.minZ) s.minZ = z;
          if (z > s.maxZ) s.maxZ = z;
        }
      }
    }

    for (let i = 1; i <= count; i++) {
      const s = stats[i];
      if (s.count === 0) continue;
      s.cx = s.sumX / s.count; s.cy = s.sumY / s.count; s.cz = s.sumZ / s.count;
    }

    const centerZ0 = Math.floor(d * 0.25), centerZ1 = Math.floor(d * 0.75);
    const candidates = Object.entries(stats)
      .filter(([, s]) => s.count > 200 && s.cz >= centerZ0 && s.cz <= centerZ1)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 4);

    const chambers = {};
    if (candidates.length >= 1) {
      const patientOrientation = _getPatientOrientation(orientation);
      const isFlippedX = patientOrientation.isFlippedX;
      
      const byX = [...candidates].sort((a, b) => 
        isFlippedX ? b[1].cx - a[1].cx : a[1].cx - b[1].cx
      );
      
      const leftTwo = byX.slice(0, Math.ceil(byX.length / 2));
      const rightTwo = byX.slice(Math.ceil(byX.length / 2));
      const leftLow = leftTwo.sort((a, b) => b[1].cz - a[1].cz)[0];
      const leftHigh = leftTwo.sort((a, b) => a[1].cz - b[1].cz)[0];
      const rightLow = rightTwo.sort((a, b) => b[1].cz - a[1].cz)[0];
      const rightHigh = rightTwo.sort((a, b) => a[1].cz - b[1].cz)[0];

      const assignments = [
        [leftLow?.[0], 1, 'LV'], [rightLow?.[0], 2, 'RV'],
        [leftHigh?.[0], 3, 'LA'], [rightHigh?.[0], 4, 'RA'],
      ];

      // O(N) через mapping array
      const labelToChamber = new Uint8Array(count + 1);
      
      for (const [lblStr, labelVal, name] of assignments) {
        if (!lblStr) continue;
        const lbl = parseInt(lblStr);
        labelToChamber[lbl] = labelVal;
        const s = stats[lbl];
        chambers[name] = {
          voxels: s.count,
          volumeMl: Math.round(s.count * spacing[0] * spacing[1] * spacing[2] / 1000 * 10) / 10,
          centroid: [Math.round(s.cx), Math.round(s.cy), Math.round(s.cz)],
        };
      }
      
      for (let i = 0; i < total; i++) {
        const lbl = labels[i];
        if (lbl > 0) result[i] = labelToChamber[lbl];
      }
    }

    onProgress?.(100);
    return { mask: result, hasContrast, chambers };
  } catch (e) { log.warn('[aiModule] segmentCardiacChambers failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// SAM — SEGMENT ANYTHING MODEL
// ══════════════════════════════════════════════════════════════

let _samImageEmbedding = null;
let _samCacheKey = null;

function preprocessForSAM(pixelData, width, height, windowing = { center: 40, width: 400 }) {
  const targetSize = 1024;
  const canvas = new OffscreenCanvas(targetSize, targetSize);
  const ctx = canvas.getContext('2d');
  const low = windowing.center - windowing.width / 2;
  const high = windowing.center + windowing.width / 2;
  const range = windowing.width || 1;

  const normalized = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < pixelData.length; i++) {
    const v = Math.round((Math.max(low, Math.min(high, pixelData[i])) - low) / range * 255);
    normalized[i * 4] = v; normalized[i * 4 + 1] = v;
    normalized[i * 4 + 2] = v; normalized[i * 4 + 3] = 255;
  }

  const srcCanvas = new OffscreenCanvas(width, height);
  const srcCtx = srcCanvas.getContext('2d');
  srcCtx.putImageData(new ImageData(normalized, width, height), 0, 0);
  ctx.drawImage(srcCanvas, 0, 0, targetSize, targetSize);

  const imgData = ctx.getImageData(0, 0, targetSize, targetSize);
  const tensor = new Float32Array(3 * targetSize * targetSize);
  const mean = [123.675, 116.28, 103.53];
  const std = [58.395, 57.12, 57.375];

  for (let i = 0; i < targetSize * targetSize; i++) {
    tensor[0 * targetSize * targetSize + i] = (imgData.data[i * 4] - mean[0]) / std[0];
    tensor[1 * targetSize * targetSize + i] = (imgData.data[i * 4 + 1] - mean[1]) / std[1];
    tensor[2 * targetSize * targetSize + i] = (imgData.data[i * 4 + 2] - mean[2]) / std[2];
  }
  return { tensor, scaleX: targetSize / width, scaleY: targetSize / height };
}

export async function samEncodeImage(pixelData, width, height, imageId, windowing = { center: 40, width: 400 }) {
  try {
    const cacheKey = `${imageId}_${windowing.center}_${windowing.width}`;
    if (imageId && cacheKey === _samCacheKey && _samImageEmbedding) return _samImageEmbedding;

    const session = await loadModel('medsam-encoder');
    const { tensor, scaleX, scaleY } = preprocessForSAM(pixelData, width, height, windowing);
    const inputTensor = new ort.Tensor('float32', tensor, [1, 3, 1024, 1024]);
    const result = await session.run({ [session.inputNames[0]]: inputTensor });

    _samImageEmbedding = { embedding: result[session.outputNames[0]], scaleX, scaleY, width, height };
    _samCacheKey = cacheKey;
    return _samImageEmbedding;
  } catch (e) { log.warn('[aiModule] samEncodeImage failed:', e.message); return null; }
}

export async function samDecodePoints(points, embedding) {
  try {
    const session = await loadModel('medsam-decoder');
    const pointCoords = new Float32Array(points.length * 2);
    const pointLabels = new Float32Array(points.length);

    for (let i = 0; i < points.length; i++) {
      pointCoords[i * 2] = points[i].x * embedding.scaleX;
      pointCoords[i * 2 + 1] = points[i].y * embedding.scaleY;
      pointLabels[i] = points[i].label;
    }

    const feeds = {
      image_embeddings: embedding.embedding,
      point_coords: new ort.Tensor('float32', pointCoords, [1, points.length, 2]),
      point_labels: new ort.Tensor('float32', pointLabels, [1, points.length]),
      mask_input: new ort.Tensor('float32', new Float32Array(256 * 256), [1, 1, 256, 256]),
      has_mask_input: new ort.Tensor('float32', new Float32Array([0]), [1]),
      orig_im_size: new ort.Tensor('float32', new Float32Array([1024, 1024]), [2]),
    };

    const result = await session.run(feeds);
    const masks = result[session.outputNames[0]].data;
    const ious = result[session.outputNames[1]].data;
    const bestIdx = Array.from(ious).indexOf(Math.max(...ious));
    const maskSize = Math.round(masks.length / 3);
    const bestMask = masks.slice(bestIdx * maskSize, (bestIdx + 1) * maskSize);

    const binary = new Uint8Array(bestMask.length);
    for (let i = 0; i < bestMask.length; i++) binary[i] = bestMask[i] > 0 ? 1 : 0;

    return {
      mask: binary,
      maskWidth: Math.round(Math.sqrt(maskSize)),
      maskHeight: Math.round(Math.sqrt(maskSize)),
      iou: Math.max(...ious),
      origWidth: embedding.width,
      origHeight: embedding.height,
    };
  } catch (e) { log.warn('[aiModule] samDecodePoints failed:', e.message); return {}; }
}

export async function samSegmentVolume(pixels, w, h, d, startZ, points, windowing) {
  try {
    log.debug(`[AI] 3D SAM tracking from Z=${startZ}`);
    const resultMask3D = new Uint8Array(w * h * d);
    const sliceSize = w * h;

    const segmentSlice = async (z, inputPoints) => {
      const slicePixels = pixels.subarray(z * sliceSize, (z + 1) * sliceSize);
      const emb = await samEncodeImage(slicePixels, w, h, `vol_${z}`, windowing);
      const result = await samDecodePoints(inputPoints, emb);
      return result.mask;
    };

    const getPromptFromMask = (mask2d) => {
      let minX = w, maxX = 0, minY = h, maxY = 0, hasMask = false;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (mask2d[y * w + x]) {
            hasMask = true;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      return hasMask ? [{ x: (minX + maxX) / 2, y: (minY + maxY) / 2, label: 1 }] : null;
    };

    let currentMask = await segmentSlice(startZ, points);
    resultMask3D.set(currentMask, startZ * sliceSize);

    let prompt = getPromptFromMask(currentMask);
    for (let z = startZ + 1; z < d && prompt; z++) {
      currentMask = await segmentSlice(z, prompt);
      prompt = getPromptFromMask(currentMask);
      if (prompt) resultMask3D.set(currentMask, z * sliceSize);
    }

    currentMask = await segmentSlice(startZ, points);
    prompt = getPromptFromMask(currentMask);
    for (let z = startZ - 1; z >= 0 && prompt; z--) {
      currentMask = await segmentSlice(z, prompt);
      prompt = getPromptFromMask(currentMask);
      if (prompt) resultMask3D.set(currentMask, z * sliceSize);
    }

    log.debug('[AI] 3D SAM tracking complete');
    return resultMask3D;
  } catch (e) { log.warn('[aiModule] samSegmentVolume failed:', e.message); return new Uint8Array(); }
}

export function clearSamCache() { _samImageEmbedding = null; _samCacheKey = null; }

// ══════════════════════════════════════════════════════════════
// SMART REGION GROWING (FIXED BFS)
// ══════════════════════════════════════════════════════════════

export async function smartRegionGrowing(pixels, w, h, d, startX, startY, startZ, tolerance = 50, gradientThreshold = 100) {
  try {
    const total = w * h * d;
    const sliceSize = w * h;
    const mask = new Uint8Array(total);
    const queue = new Int32Array(total);
    const idx = (x, y, z) => z * sliceSize + y * w + x;
    const startIdx = startZ * sliceSize + startY * w + startX;
    const startHU = pixels[startIdx];

    let head = 0, tail = 0;
    
    // Помечаем visited ДО enqueue
    mask[startIdx] = 1;
    queue[tail++] = startIdx;
    
    let voxelsCount = 1;
    let lastYield = performance.now();

    while (head < tail && voxelsCount < 500000) {
      if (performance.now() - lastYield > 15) { await _yieldToMain(); lastYield = performance.now(); }

      const curr = queue[head++];
      const cz = Math.floor(curr / sliceSize);
      const cy = Math.floor((curr % sliceSize) / w);
      const cx = curr % w;
      const currHU = pixels[curr];

      const neighbors = [
        cx > 0 ? idx(cx - 1, cy, cz) : -1, cx < w - 1 ? idx(cx + 1, cy, cz) : -1,
        cy > 0 ? idx(cx, cy - 1, cz) : -1, cy < h - 1 ? idx(cx, cy + 1, cz) : -1,
        cz > 0 ? idx(cx, cy, cz - 1) : -1, cz < d - 1 ? idx(cx, cy, cz + 1) : -1
      ];

      for (const ni of neighbors) {
        if (ni >= 0 && mask[ni] === 0) {
          const niHU = pixels[ni];
          if (Math.abs(niHU - startHU) <= tolerance && Math.abs(niHU - currHU) <= gradientThreshold) {
            mask[ni] = 1;
            queue[tail++] = ni;
            voxelsCount++;
          } else {
            mask[ni] = 2;
          }
        }
      }
    }

    for (let i = 0; i < total; i++) if (mask[i] === 2) mask[i] = 0;
    return { mask, voxelsCount };
  } catch (e) { log.warn('[aiModule] smartRegionGrowing failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// LUNG NODULES DETECTION (FIXED BFS)
// ══════════════════════════════════════════════════════════════

export async function detectLungNodules(pixels, w, h, d, spacing, options = {}) {
  try {
    const { threshold = 0.7, onProgress } = options;
    const sliceSize = w * h;
    const nodules = [];
    let lastYield = performance.now();
    
    const { queueX, queueY, visitedMarks, visitId } = _getNoduleBfsBuffers(sliceSize);

    for (let z = 2; z < d - 2; z += 3) {
      if (performance.now() - lastYield > 15) {
        onProgress?.(Math.round((z / d) * 100));
        await _yieldToMain();
        lastYield = performance.now();
      }

      for (let y = 10; y < h - 10; y += 5) {
        for (let x = 10; x < w - 10; x += 5) {
          const idx = z * sliceSize + y * w + x;
          const hu = pixels[idx];
          if (hu < -200 || hu > 150) continue;

          let lungNeighbors = 0, totalNeighbors = 0;
          for (let dy = -5; dy <= 5; dy += 5) {
            for (let dx = -5; dx <= 5; dx += 5) {
              if (dx === 0 && dy === 0) continue;
              totalNeighbors++;
              const nhu = pixels[idx + dy * w + dx];
              if (nhu >= -950 && nhu <= -500) lungNeighbors++;
            }
          }

          if (totalNeighbors > 0 && lungNeighbors / totalNeighbors >= threshold) {
            let size = 0, sumHU = 0;
            let head = 0, tail = 0;
            
            const startVk = y * w + x;
            
            // Помечаем visited ДО enqueue
            visitedMarks[startVk] = visitId;
            queueX[tail] = x;
            queueY[tail] = y;
            tail++;

            while (head < tail && size < 2000) {
              const cx = queueX[head];
              const cy = queueY[head];
              head++;

              const vk = cy * w + cx;
              const ci = z * sliceSize + vk;
              if (ci < 0 || ci >= pixels.length) continue;
              const chu = pixels[ci];
              if (chu < -200 || chu > 150) continue;

              size++; sumHU += chu;
              
              if (cx > 0) {
                const nvk = cy * w + (cx - 1);
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx - 1; queueY[tail] = cy; tail++;
                }
              }
              if (cx < w - 1) {
                const nvk = cy * w + (cx + 1);
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx + 1; queueY[tail] = cy; tail++;
                }
              }
              if (cy > 0) {
                const nvk = (cy - 1) * w + cx;
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx; queueY[tail] = cy - 1; tail++;
                }
              }
              if (cy < h - 1) {
                const nvk = (cy + 1) * w + cx;
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx; queueY[tail] = cy + 1; tail++;
                }
              }
            }

            if (size >= 5 && size < 1500) {
              const areaMm2 = size * spacing[0] * spacing[1];
              const diamMm = parseFloat(Math.sqrt(areaMm2 * 4 / Math.PI).toFixed(1));
              const meanHU = Math.round(sumHU / size);

              let perimeter = 0;
              for (let q = 0; q < head; q++) {
                const px = queueX[q];
                const py = queueY[q];
                if (px === 0 || visitedMarks[py * w + (px - 1)] !== visitId) perimeter++;
                if (px >= w - 1 || visitedMarks[py * w + (px + 1)] !== visitId) perimeter++;
                if (py === 0 || visitedMarks[(py - 1) * w + px] !== visitId) perimeter++;
                if (py >= h - 1 || visitedMarks[(py + 1) * w + px] !== visitId) perimeter++;
              }

              const circularity = (4 * Math.PI * size) / (perimeter * perimeter || 1);
              if (circularity < 0.4) continue;

              let lungRADS;
              if (diamMm < 4) lungRADS = 'Lung-RADS 2 (доброкачественный)';
              else if (diamMm < 6) lungRADS = 'Lung-RADS 3 (вероятно доброкачественный)';
              else if (diamMm < 8) lungRADS = 'Lung-RADS 4A (подозрительный)';
              else lungRADS = 'Lung-RADS 4B (очень подозрительный)';

              const isDupe = nodules.some(n => Math.abs(n.x - x) < 15 && Math.abs(n.y - y) < 15 && Math.abs(n.z - z) < 5);
              if (!isDupe) nodules.push({ x, y, z, voxels: size, diameterMm: diamMm, meanHU, lungRADS });
            }
          }
        }
      }
    }

    nodules.sort((a, b) => b.diameterMm - a.diameterMm);
    onProgress?.(100);
    return { totalFound: nodules.length, nodules: nodules.slice(0, 20) };
  } catch (e) { log.warn('[aiModule] detectLungNodules failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// ADVANCED LUNG & STROKE ANALYSIS (FIXED CCL)
// ══════════════════════════════════════════════════════════════

export async function advancedLungAnalysis(pixels, lungMask, w, h, d, spacing, options = {}) {
  try {
    const { onProgress } = options;
    const voxelVolMl = (spacing[0] * spacing[1] * spacing[2]) / 1000;
    let totalLungVoxels = 0, emphysemaVoxels = 0, normalVoxels = 0;
    let ggoVoxels = 0, consolidationVoxels = 0, fibrosisVoxels = 0;

    const emphysemaMask = new Uint8Array(pixels.length);
    let lastYield = performance.now();

    for (let i = 0; i < pixels.length; i++) {
      if ((i & 0x1FFFF) === 0 && performance.now() - lastYield > 15) {
        onProgress?.(Math.round((i / pixels.length) * 50));
        await _yieldToMain();
        lastYield = performance.now();
      }
      if (lungMask[i] > 0) {
        totalLungVoxels++;
        const hu = pixels[i];
        if (hu < -950) { emphysemaVoxels++; emphysemaMask[i] = 1; }
        else if (hu < -700) normalVoxels++;
        else if (hu < -300) ggoVoxels++;
        else if (hu < 50) consolidationVoxels++;
        else if (hu <= 150) fibrosisVoxels++;
      }
    }

    if (totalLungVoxels === 0) return { error: "Маска легких пуста" };

    const totalVolMl = totalLungVoxels * voxelVolMl;
    const p = (v) => parseFloat(((v / totalLungVoxels) * 100).toFixed(1));

    // Сохраняем labels из CCL
    const { count: bullaeCount, labels } = await connectedComponents3D(emphysemaMask, w, h, d, prog => onProgress?.(50 + prog * 0.5));

    const bullaeStats = new Int32Array(bullaeCount + 1);
    for (let i = 0; i < labels.length; i++) {
      if (labels[i] > 0) bullaeStats[labels[i]]++;
    }

    let giantBullaeCount = 0;
    for (let i = 1; i <= bullaeCount; i++) {
      if ((bullaeStats[i] * voxelVolMl) > 1.0) giantBullaeCount++;
    }

    onProgress?.(100);
    return {
      lungVolumeMl: Math.round(totalVolMl),
      fractions: {
        emphysema: p(emphysemaVoxels), normal: p(normalVoxels),
        groundGlassOpacities: p(ggoVoxels), consolidation: p(consolidationVoxels),
        fibrosis: p(fibrosisVoxels)
      },
      findings: {
        pneumoniaSeverity: p(ggoVoxels + consolidationVoxels) > 5 ? 'Обнаружены инфильтраты. Подозрение на пневмонию.' : 'Норма',
        fibrosisRisk: p(fibrosisVoxels) > 3 ? 'Повышена плотность интерстиция (риск саркоидоза/фиброза).' : 'Норма',
        giantBullaeFound: giantBullaeCount > 0 ? `Найдено ${giantBullaeCount} крупных булл (>1 см³). Риск пневмоторакса.` : 'Крупных булл не обнаружено'
      }
    };
  } catch (e) { log.warn('[aiModule] advancedLungAnalysis failed:', e.message); return {}; }
}

export async function classifyStroke(pixels, w, h, d, options = {}) {
  try {
    const { onProgress } = options;
    const sliceSize = w * h;
    const midX = Math.floor(w / 2);
    let leftSum = 0, leftCnt = 0, rightSum = 0, rightCnt = 0;
    let highDensityLeft = 0, highDensityRight = 0;

    const zStart = Math.floor(d * 0.2), zEnd = Math.floor(d * 0.8);
    let lastYield = performance.now();

    for (let z = zStart; z < zEnd; z++) {
      if (performance.now() - lastYield > 15) {
        onProgress?.(Math.round(((z - zStart) / (zEnd - zStart)) * 100));
        await _yieldToMain();
        lastYield = performance.now();
      }

      for (let y = Math.floor(h * 0.15); y < Math.floor(h * 0.85); y++) {
        for (let x = Math.floor(w * 0.1); x < Math.floor(w * 0.9); x++) {
          const hu = pixels[z * sliceSize + y * w + x];
          if (hu < 10 || hu > 100) continue;
          if (x < midX) { leftSum += hu; leftCnt++; if (hu > 60) highDensityLeft++; }
          else { rightSum += hu; rightCnt++; if (hu > 60) highDensityRight++; }
        }
      }
    }

    onProgress?.(100);
    if (leftCnt < 1000 || rightCnt < 1000) {
      return { classification: 'Не определено', confidence: 0, leftMeanHU: 0, rightMeanHU: 0, asymmetryHU: 0, note: 'Недостаточно данных мозговой ткани.' };
    }

    const leftMeanHU = parseFloat((leftSum / leftCnt).toFixed(1));
    const rightMeanHU = parseFloat((rightSum / rightCnt).toFixed(1));
    const asymmetryHU = parseFloat(Math.abs(leftMeanHU - rightMeanHU).toFixed(1));
    const highDensityAsym = Math.abs(highDensityLeft - highDensityRight) / Math.max(highDensityLeft, highDensityRight, 1);

    let classification, confidence;
    if (highDensityAsym > 0.5 && Math.max(highDensityLeft, highDensityRight) > 500) {
      classification = 'Геморрагический';
      confidence = Math.min(95, Math.round(60 + highDensityAsym * 35));
    } else if (asymmetryHU > 3) {
      classification = 'Ишемический';
      confidence = Math.min(85, Math.round(40 + asymmetryHU * 10));
    } else {
      classification = 'Норма';
      confidence = Math.min(90, Math.round(70 + (3 - asymmetryHU) * 10));
    }

    const side = leftMeanHU > rightMeanHU ? 'левое' : 'правое';
    const note = classification === 'Норма' ? 'Асимметрии полушарий не обнаружено' : `Изменения преобладают в ${side} полушарии.`;

    return { classification, confidence, leftMeanHU, rightMeanHU, asymmetryHU, highDensityLeft, highDensityRight, note };
  } catch (e) { log.warn('[aiModule] classifyStroke failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// LONGEVITY SCREENING
// ══════════════════════════════════════════════════════════════

export async function cirrhosisRiskIndex(pixels, w, h, d, spacing) {
  try {
    const voxelVolMl = (spacing[0] * spacing[1] * spacing[2]) / 1000;
    const sliceSize = w * h;
    const midX = Math.floor(w / 2);
    let liverVoxels = 0, spleenVoxels = 0, ascitesVoxels = 0;
    let lastYield = performance.now();

    for (let z = Math.floor(d * 0.3); z < d; z++) {
      if (performance.now() - lastYield > 15) { await _yieldToMain(); lastYield = performance.now(); }
      for (let y = Math.floor(h * 0.3); y < Math.floor(h * 0.8); y++) {
        for (let x = Math.floor(w * 0.1); x < Math.floor(w * 0.9); x++) {
          const hu = pixels[z * sliceSize + y * w + x];
          if (x < midX && hu >= 40 && hu <= 80) liverVoxels++;
          else if (x > midX && hu >= 40 && hu <= 60) spleenVoxels++;
          else if (y > Math.floor(h * 0.6) && hu >= 0 && hu <= 20) ascitesVoxels++;
        }
      }
    }

    const liverVolMl = liverVoxels * voxelVolMl;
    const spleenVolMl = spleenVoxels * voxelVolMl;
    const ascitesVolMl = ascitesVoxels * voxelVolMl;
    if (liverVolMl < 200 && spleenVolMl < 50) return { error: "Недостаточно данных брюшной полости" };

    const spleenToLiverRatio = spleenVolMl / (liverVolMl || 1);
    let riskLevel = "Низкий";
    const notes = [];
    if (spleenVolMl > 500) { notes.push("Спленомегалия."); riskLevel = "Средний"; }
    if (spleenToLiverRatio > 0.5) { notes.push("Сморщивание печени."); riskLevel = "Высокий"; }
    if (ascitesVolMl > 100) { notes.push("Асцит."); riskLevel = "Очень высокий"; }

    return {
      liverEstimatedVolMl: Math.round(liverVolMl),
      spleenEstimatedVolMl: Math.round(spleenVolMl),
      ascitesEstimatedVolMl: Math.round(ascitesVolMl),
      spleenToLiverRatio: spleenToLiverRatio.toFixed(2),
      cirrhosisRisk: riskLevel,
      clinicalNotes: notes.length > 0 ? notes.join(' ') : "Признаков портальной гипертензии не обнаружено."
    };
  } catch (e) { log.warn('[aiModule] cirrhosisRiskIndex failed:', e.message); return {}; }
}

export function bodyCompositionAnalysis(pixels, w, h, d, spacing, options = {}) {
  const { zL3 = Math.floor(d / 2) } = options;
  const targetZ = Math.max(0, Math.min(d - 1, zL3));

  let viscFat = 0, subcFat = 0, muscle = 0;
  const cx = w / 2, cy = h / 2, maxR = w * 0.35;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const hu = pixels[targetZ * w * h + y * w + x];
      if (hu >= -190 && hu <= -30) {
        if (Math.hypot(x - cx, y - cy) > maxR) subcFat++; else viscFat++;
      } else if (hu >= -29 && hu <= 150) {
        muscle++;
      }
    }
  }
  const totalFat = viscFat + subcFat;
  const smi = muscle / (w * h);
  return {
    evaluatedSliceZ: targetZ,
    visceralFatRatio: totalFat > 0 ? (viscFat / totalFat).toFixed(2) : '0.00',
    sarcopeniaRisk: smi < 0.1 ? 'Высокий (Снижение массы)' : 'Норма'
  };
}

// ══════════════════════════════════════════════════════════════
// UTILITIES
// ══════════════════════════════════════════════════════════════

export function detectBodyPart(pixels, w, h, d) {
  let airCount = 0, boneCount = 0;
  const total = pixels.length;
  const step = Math.floor(total / 100000) || 1;
  for (let i = 0; i < total; i += step) {
    const hu = pixels[i];
    if (hu < -500) airCount++;
    else if (hu > 250) boneCount++;
  }
  const samples = total / step;
  const airRatio = airCount / samples;
  const boneRatio = boneCount / samples;
  if (boneRatio > 0.15 && airRatio < 0.05) return 'HEAD';
  if (airRatio > 0.3) return 'CHEST';
  return 'ABDOMEN';
}

export function autoContourByThreshold(pixels, w, h, d, spacing, options = {}) {
  const { huMin = -100, huMax = 200, minVoxels = 100 } = options;
  const voxelVol = spacing[0] * spacing[1] * spacing[2];
  const sliceSize = w * h;
  let totalCount = 0;
  const sliceCounts = new Map();
  for (let i = 0; i < pixels.length; i++) {
    if (pixels[i] >= huMin && pixels[i] <= huMax) {
      totalCount++;
      const z = Math.floor(i / sliceSize);
      sliceCounts.set(z, (sliceCounts.get(z) || 0) + 1);
    }
  }
  let contourCount = 0;
  for (const [, cnt] of sliceCounts) { if (cnt >= minVoxels) contourCount++; }
  return {
    contourCount, totalVoxels: totalCount,
    volumeMl: parseFloat((totalCount * voxelVol / 1000).toFixed(1)),
    slicesWithContours: sliceCounts.size,
  };
}

export function extractMesh(mask, w, h, d, spacing) {
  log.warn('[AI] extractMesh: Полноценный Marching Cubes вынесен в отдельный модуль MC33.');
  return {
    vertices: new Float32Array(0),
    indices: new Uint32Array(0),
    note: 'STL export requires full marching cubes implementation (see MC33 module)'
  };
}

// ══════════════════════════════════════════════════════════════
// LIFECYCLE & DIAGNOSTICS
// ══════════════════════════════════════════════════════════════

export async function releaseModels() {
  try {
    for (const session of _sessions.values()) {
      if (session.release) await session.release();
    }
    _sessions.clear();
    clearSamCache();

    _cclLabelsPool = null;
    _cclQueuePool = null;
    _resizeBufPool = null;
    _resizeBufInUse = false;
    _noduleBfsQueueX = null;
    _noduleBfsQueueY = null;
    _noduleVisitedMarks = null;
    _noduleVisitId = 0;

    log.debug('[AI] Все сессии ONNX WebGPU и буферы памяти выгружены');
  } catch (e) { log.warn('[aiModule] releaseModels failed:', e.message); }
}

export function getAIModuleState() {
  return {
    ortLoaded: !!ort,
    loadedModels: [..._sessions.keys()],
    loadedModelCount: _sessions.size,
    availableModels: Object.keys(_modelUrls),
    availableModelCount: Object.keys(_modelUrls).length,
    aliases: { ..._aliasMap },
    algorithmicFallbacks: [..._algorithmicFallbacks],
    memoryPoolAllocated: !!_cclLabelsPool,
    memoryPoolSizeMB: _cclLabelsPool ? +((_cclLabelsPool.byteLength * 2 + (_resizeBufPool?.byteLength || 0)) / 1048576).toFixed(1) : 0,
  };
}

export function listAvailableModels() {
  return Object.entries(_modelUrls).map(([id, url]) => ({
    id, url, loaded: _sessions.has(id), hasAlgorithmicFallback: _algorithmicFallbacks.has(id),
  }));
}

export function listAICapabilities() {
  return [
    'calciumScoring', 'vulnerablePlaqueDetect', 'ejectionFraction',
    'lungNoduleDetection', 'emphysemaQuantification', 'covidPneumonia',
    'strokeAnalysis', 'brainAtrophy',
    'tumorKinetics', 'bodyCompositionAnalysis',
    'cirrhosisRiskIndex', 'aortaAneurysmScreening', 'incidentalFindingsScreening',
    'detectBodyPart', 'extractMesh', 'samSegmentation',
    'runSegmentation', 'runSegmentation3DLoop',
  ];
}

export function getModelUrl(modelId) { return _modelUrls[_canonicalModelId(modelId)] || null; }
export function isOrtLoaded() { return !!ort; }

export { connectedComponents3D, _getCCLPools };